import React, { useEffect, useRef } from 'react';
import { Animated, TouchableOpacity, View } from 'react-native';
import { Text } from '@/components/Text';
import { styles } from './style';
import { Icon, IconName } from '@/components/Icon';

export type NotificationType = 'success' | 'error' | 'info' | 'alert';

export interface NotificationProps {
  message?: string;
  title?: string;
  type: NotificationType;
  duration?: number;
  onHide: () => void;
  children?: React.ReactNode;
}

const iconMap: Record<'success' | 'info' | 'alert' | 'error', IconName> = {
  success: 'checkCircleAlert',
  info: 'circleInfo',
  alert: 'alertTriangle',
  error: 'closecircle',
};

const Notification: React.FC<NotificationProps> = ({
  message,
  title,
  type = 'info',
  duration = 3000,
  onHide,
  children,
}) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-20)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();

    const timer = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        requestAnimationFrame(() => onHide());
      });
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onHide, opacity, translateY]);

  return (
    <Animated.View
      style={[
        styles.notification,
        styles.notificationBase,
        styles[type],
        {
          opacity,
          transform: [{ translateY }],
        },
      ]}
    >
      <View style={[styles.content, { alignItems: 'flex-start' }]}>
        <Icon
          name={iconMap[type]}
          color={styles[`${type}Text`].color}
          style={styles.icon}
          size={24}
        />
        <View style={[styles.children, !message && styles.noMessageChildren]}>
          {title && (
            <Text style={[styles[`${type}Text`], styles.title]}>{title}</Text>
          )}
          {message && (
            <Text style={[styles.message, styles[`${type}Text`]]}>
              {message}
            </Text>
          )}
          {children && (
            <View
              style={
                message
                  ? { marginRight: 'auto' }
                  : { marginLeft: 'auto', paddingLeft: 10 }
              }
            >
              {children}
            </View>
          )}
        </View>
        <TouchableOpacity onPress={onHide} style={styles.closeContainer}>
          <Icon name={'close'} color={styles.infoText.color} size={24} />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

export default Notification;
